import React, { useState, useEffect, useCallback } from "react";
import { Mail, Lock, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function ForgotPassword() {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const [step, setStep] = useState(null); // null, 'email', 'otp', or 'password'
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timer, setTimer] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  // Send OTP (called when step changes to 'otp')
  const handleSendOTP = useCallback(async (emailToSend) => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_URL}/api/auth/send-reset-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: emailToSend,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Failed to send OTP");
        setStep("email");
        return;
      }

      setSuccess("If the email is valid, you will receive a code shortly.");
      setTimer(90); 
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Network error. Please try again.");
      setStep("email");
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Initialize step based on authentication status
  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated && user?.email) {
      // Logged in user: Set email state and move to OTP step
      setEmail(user.email);
      setStep("otp");
    } else {
      // Not logged in: Show email step
      setStep("email");
    }
  }, [authLoading, isAuthenticated, user]);

  // Send OTP when step changes to 'otp'
  useEffect(() => {
    if (step === "otp" && email) {
      handleSendOTP(email);
    }
  }, [step, email, handleSendOTP]);

  // Timer for OTP resend
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const validateEmail = (e) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(e);
  };

  // Case: Not logged in - Verify email exists, then move to OTP step
  const handleEmailSubmit = async () => {
    setError("");
    setSuccess("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      // Verify email exists in database
      const verifyResponse = await fetch(
        `${API_URL}/api/auth/verify-reset-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.toLowerCase().trim(),
          }),
        }
      );

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyData.success) {
        setError(
          verifyData.message || "No account found with this email."
        );
        return;
      }

      // Email verified! Now move to OTP step
      // OTP will be sent automatically when step changes to 'otp'
      setEmail(email.toLowerCase().trim());
      setSuccess("Request processed. Sending code...");
      setTimeout(() => {
        setSuccess("");
        setStep("otp");
        // Don't set timer here, it will be set when OTP is sent
      }, 500);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    setError("");
    setSuccess("");

    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-reset-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          otp: otp,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Invalid OTP");
        return;
      }

      setSuccess("OTP verified successfully");
      setTimeout(() => {
        setSuccess("");
        setStep("password");
      }, 500);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    setError("");
    setSuccess("");

    if (!password || !confirmPassword) {
      setError("Please enter both passwords");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8 || password.length > 50) {
      setError("Password must be between 8 and 50 characters long");
      return;
    }

    // Check for common passwords
    const commonPasswords = ["password", "12345678", "qwertyuiop", "abc12345"];
    if (commonPasswords.includes(password.toLowerCase())) {
      setError("This password is too common. Please choose a stronger one.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: email,
          otp: otp,
          newPassword: password,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Failed to reset password");
        return;
      }

      setSuccess("Password reset successfully!");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/api/auth/send-reset-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || "Failed to resend OTP");
        return;
      }

      setSuccess("If the email is valid, a new code has been sent.");
      setTimer(90);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setTimer(0);
  };

  // Show loading state while auth is being checked
  if (authLoading || step === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-30 -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-50 rounded-full blur-3xl opacity-40 -ml-48 -mb-48"></div>
        </div>
        <div className="w-12 h-12 border-3 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center sm:p-4 p-1">
      {/* Decorative circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-30 -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-50 rounded-full blur-3xl opacity-40 -ml-48 -mb-48"></div>
      </div>

      <div className="relative w-full max-w-md mx-auto">
        {/* Card */}
        <div className="bg-white rounded-lg border-0 sm:border-2 sm:border-gray-400 p-4 sm:p-8 transition-all duration-300">
          {/* Header */}
          <div className="mb-6 sm:mb-8 transition-all duration-300">
            <div className="w-12 sm:w-16 h-12 sm:h-16 bg-white border-2 border-black rounded-full flex items-center justify-center mb-3 sm:mb-4 mx-auto">
              <Lock size={24} className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
              {step === "email"
                ? "Reset Your Password"
                : step === "otp"
                ? "Verify OTP"
                : "Set New Password"}
            </h1>
            <p className="text-gray-600 text-center text-xs sm:text-sm">
              {step === "email"
                ? isAuthenticated
                  ? "We'll send a verification code to your email address."
                  : "Enter the email address associated with your account."
                : step === "otp"
                ? `If an account is linked to ${email}, a 6-digit code will be sent. Enter it below.`
                : "Create a strong new password for your account."}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-fade-in">
              <AlertCircle
                size={20}
                className="text-red-600 flex-shrink-0 mt-0.5"
              />
              <p className="text-red-700 text-xs sm:text-sm">{error}</p>
            </div>
          )}

          {/* Success Alert */}
          {success && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 animate-fade-in">
              <CheckCircle2
                size={20}
                className="text-green-600 flex-shrink-0 mt-0.5"
              />
              <p className="text-green-700 text-xs sm:text-sm">{success}</p>
            </div>
          )}

          {/* Email Step */}
          {step === "email" && !isAuthenticated && (
            <div className="space-y-4 sm:space-y-6 animate-fade-in">
              <div>
                <label className="block text-gray-900 text-xs sm:text-sm font-semibold mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleEmailSubmit()}
                    placeholder="you@example.com"
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl pl-12 pr-4 py-2 sm:py-3 text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <button
                onClick={handleEmailSubmit}
                disabled={loading}
                className="w-full bg-[#b0d284] disabled:from-gray-400 disabled:to-gray-500 text-black font-semibold py-2 sm:py-3 text-sm sm:text-base rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs sm:text-sm">Verifying...</span>
                  </span>
                ) : (
                  "Continue"
                )}
              </button>

              {/* Footer */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                <p className="text-gray-600 text-xs sm:text-sm text-center">
                  Remember your password?{" "}
                  <a
                    href="/"
                    className="text-green-600 hover:text-green-700 font-semibold transition-colors"
                  >
                    Sign in here
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* OTP Step */}
          {step === "otp" && (
            <div className="space-y-4 sm:space-y-4 animate-fade-in">
              {/* Email Display */}
              <div className="text-center">
                <p className="text-xs sm:text-sm text-gray-800">
                  Verification code sent to{" "}
                  <span className="font-semibold">{email}</span>
                </p>
              </div>

              {/* OTP Input */}
              <div>
                <label className="block text-gray-900 text-xs sm:text-sm font-semibold mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyPress={(e) =>
                    e.key === "Enter" && otp.length === 6 && handleVerifyOTP()
                  }
                  placeholder="000000"
                  maxLength="6"
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-2 sm:py-3 text-gray-900 placeholder-gray-500 text-xl sm:text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                />
                <p className="text-xs text-gray-600 mt-2 text-center">
                  {timer > 0 ? (
                    <>
                      Resend OTP in{" "}
                      <span className="text-green-600 font-semibold">
                        {timer}s
                      </span>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading}
                      className="text-green-600 hover:text-green-700 font-semibold transition-colors disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  )}
                </p>
              </div>

              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
                className="w-full bg-[#b0d284] disabled:from-gray-400 disabled:to-gray-500 text-black font-semibold py-2 sm:py-3 text-sm sm:text-base rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs sm:text-sm">Verifying...</span>
                  </span>
                ) : (
                  "Verify OTP"
                )}
              </button>

              {/* Back Button */}
              {!isAuthenticated && (
                <button
                  onClick={handleBackToEmail}
                  className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 font-semibold py-2 transition-colors"
                >
                  <ArrowLeft size={18} />
                  Change Email
                </button>
              )}
            </div>
          )}

          {/* Password Step */}
          {step === "password" && (
            <div className="space-y-4 sm:space-y-6 animate-fade-in">
              {/* New Password Input */}
              <div>
                <label className="block text-gray-900 text-xs sm:text-sm font-semibold mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-2 sm:py-3 text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label className="block text-gray-900 text-xs sm:text-sm font-semibold mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleResetPassword()
                    }
                    placeholder="Confirm new password"
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-2 sm:py-3 text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                  >
                    {showConfirmPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full bg-[#b0d284] disabled:from-gray-400 disabled:to-gray-500 text-black font-semibold py-2 sm:py-3 text-sm sm:text-base rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs sm:text-sm">Resetting...</span>
                  </span>
                ) : (
                  "Reset Password"
                )}
              </button>

              {/* Back Button */}
              {!isAuthenticated && (
                <button
                  onClick={handleBackToEmail}
                  className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 font-semibold py-2 transition-colors"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>
              )}
            </div>
          )}
        </div>

        {/* Animation styles */}
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
}